import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useAuth } from "../context/AuthContext";
import { getWithRetry, postWithRetry, isUnknownAnswer } from "../lib/fetchWithRetry";
import { NOTIFICATIONS_CHANGED_EVENT, markNotificationsRead } from "../lib/notificationsRead";
import { markNav, track } from "../lib/usageTracker";

/**
 * The topbar notification centre (issue #189) — badge + panel over the
 * derived feed at `GET /api/notifications`.
 *
 * PLAYER AND COACH (#383), never admin. Layout.jsx renders this for
 * `role === "player" || role === "coach"` and renders NOTHING for admin —
 * not a bell with a 0.
 *
 * It was player-only under #189, and the reason was real at the time: every
 * event the feed could express was authored BY a coach, so a coach's own
 * feed was empty by construction and a permanent zero would have been the
 * dead-bell defect (#185's decorative bell) one layer up. #383 adds the
 * three events that run the other way — a linked player achieving a goal,
 * posting a match or season reflection, completing Identity & Vision — so
 * the coach's bell now has something to say.
 *
 * The API agrees rather than being merely trusted: `GET /api/notifications`
 * 404s a caller who holds neither a player profile nor an `active` coach
 * link roster, which is exactly the admin case this component is not
 * rendered for. A coach with an empty roster gets a legitimate 200 with
 * nothing in it — unlike the old dead bell, that state ends the moment a
 * player links to them.
 *
 * THE COPY IS ROLE-AWARE, and has to be: "feedback, goals and sessions from
 * your coach" is a sentence about the wrong person when a coach reads it.
 * The ITEMS are not — every title and link is composed server-side, so this
 * component still knows nothing about who its reader is beyond which empty
 * state to draw.
 *
 * Radix Popover, from the existing kit (`ui/popover.jsx`, already used by
 * AccessibilityMenu two elements to the right in the same topbar) — so
 * keyboard opening, focus management, Escape-to-dismiss and click-outside
 * all come from the primitive rather than being re-implemented here.
 *
 * THE PANEL IS THE UNREAD LIST (#465, Martin, decided 2026-08-25 and
 * re-confirmed 2026-08-27 — reversing the design #417 shipped). The
 * reversed design held that badge and panel answered different questions —
 * the badge "what has not been read", the panel "what is there" — so a
 * read item stayed in the list, greyed, as a "what happened this week"
 * record. Martin was shown that argument and chose the original ask both
 * times: the panel is a to-deal-with list, not an archive, so read = gone.
 * The API enforces it (`GET /api/notifications` serves only unread items,
 * with no per-item `read` flag on the wire — `routes/notifications.ts`),
 * and this component draws every row it is given as unread because that is
 * now what every row is. What the reversal consciously gave up is the
 * recent-history record; nobody has asked for a history view, so none
 * exists. Nothing becomes unreachable: every event type has a permanent
 * home on its own page, so a disappearing item is a dismissal, not a
 * deletion. This paragraph REPLACES the greyed-record reasoning rather
 * than deleting it silently, per #470's precedent — the next person
 * wondering where read notifications went should find this, not a bug.
 *
 * THREE GESTURES CLEAR THINGS — and since #465, "clear" means the item
 * leaves the panel, not that it greys:
 *   - opening the panel stamps `users.notifications_seen_at`, the bulk
 *     watermark, which acknowledges everything that existed at that moment
 *     (#189, unchanged). The rows the reader is looking at are NOT yanked
 *     mid-glance; they leave on the next open — see `onOpenChange`;
 *   - CLICKING an item marks THAT item read before navigating;
 *   - ARRIVING on the page an item points at marks that page's types read —
 *     done by the destination pages themselves through
 *     `lib/notificationsRead.js`, not here.
 * The third is what Martin actually asked for ("going to it on the
 * platform") and is the reason this component now listens for
 * `NOTIFICATIONS_CHANGED_EVENT`: it is mounted inside Layout, which mounts
 * once for the whole app and never remounts on navigation, so without a
 * nudge the badge would keep counting items the reader was just shown until
 * the next full page load.
 *
 * That nudge does NOT extend to items arriving from the server WHILE the
 * tab is in front of the reader: a coach posting feedback during a session
 * will not light the bell until the next load, and there is still no poll
 * behind every click (#185's tradeoff). What D403 (#1513) adds is the
 * return: when the tab becomes visible again or the window regains focus,
 * the bell reloads. That is the moment a phone user comes back to the app
 * after the coach acted, and there is no native app to push the news.
 * Browsers fire `visibilitychange` and `focus` together on a return, so the
 * pair is collapsed to one request (see `RETURN_RELOAD_GAP_MS`).
 */

/** A return fires both `visibilitychange` and `focus`; one reload per this gap. */
const RETURN_RELOAD_GAP_MS = 1000;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "3h ago" — a coarse, honest relative stamp. Local to this file because
 * this panel is its only consumer today; the app has no shared date
 * formatter to extend (AdminUsersPanel.jsx formats absolute dates inline
 * for its own reasons), and inventing one for a single caller is the kind
 * of premature shared helper that ends up wrong for its second user.
 *
 * Returns "" for anything unparseable rather than "Invalid Date" or a
 * plausible-looking wrong value — the caller then renders no timestamp,
 * which is the honest output when we don't know when something happened.
 */
export function relativeTime(iso, now = Date.now()) {
  if (typeof iso !== "string" || !iso) return "";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const delta = now - then;
  // A clock skew between the server's `created_at` and the browser can put
  // an event a few seconds in the future; "in 4 seconds" is noise, "just
  // now" is true enough.
  if (delta < MINUTE) return "just now";
  if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m ago`;
  if (delta < DAY) return `${Math.floor(delta / HOUR)}h ago`;
  if (delta < 7 * DAY) return `${Math.floor(delta / DAY)}d ago`;
  const d = new Date(then);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-NZ");
}

/**
 * `compact`/`surface` — PR #887 review, B2/R2 (D159, reversible by Andreas).
 *
 * `compact` (default `false`, today's shipped bell unchanged): on a
 * new-look bar (navy or the white set-up bar), the drawn glyph is 18px
 * with the tap target extending INVISIBLY to 44px via negative margin,
 * not by growing the row — the review's own finding (B2): an un-shrunk
 * 44px footprint on every one of the bar's three controls pushed the
 * whole endControls group wide enough to overlap the wordmark at
 * 360-375px (`gap: -20.84`/`-5.84` measured at those widths). Same
 * technique `AccessibilityMenu.jsx`'s own `hideTextSize` branch and
 * `AccountMenu.jsx`'s own `empty` branch use, for the same reason.
 *
 * `surface` ("navy", the default, or "white") only changes the UNREAD
 * BADGE'S colours, and only when `compact` — D159: R-003 allows no gold
 * in the bar apart from the mark, and the badge was gold unconditionally.
 * White-with-navy-text on a navy bar, navy-with-white-text on the white
 * set-up bar; the shipped (non-compact) badge is untouched.
 */
/**
 * Issue #892 (D171 X2) — the `compact` trigger's own focus-visible outline,
 * keyed to `surface` exactly like the unread badge above it. `ring-1
 * ring-ring` (the shipped, non-compact style, untouched) is `#0B305B`
 * (`index.css:91`), navy — invisible on the navy bar this control sits on
 * for every screen except `surface="white"` (Layout.jsx's `whiteTopBar`).
 * A 2px `outline` (not another `ring`, which is a `box-shadow` that grows
 * OUTWARD from the box) with a `-2px` offset draws INSIDE this control's own
 * 44px-wide (#1118; 32 before), D160 tap area instead of over its neighbour's — a `ring` here
 * clipped against the 44×barHeight box's edge-to-edge neighbours the same
 * way D160's own `rounded-full` removal was fixing. White-on-navy and
 * navy-on-white each clear WCAG 2.4.11/1.4.11's 3:1 against the bar.
 */
const COMPACT_FOCUS_RING =
  // #931 — `nw-topbar-focus` is a marker class only (no rules of its own
  // here): `index.css`'s `html.hc-theme .nw-topbar-focus:focus-visible`
  // targets exactly these four controls so the hc-theme global rule's
  // outset ring can be pulled inside without touching every other
  // focusable element on the page.
  "focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] nw-topbar-focus";
// #931, D171 X2 ruling (already-decided: "white on navy and navy on
// white" applies under hc-theme too, per-surface exactly like the non-hc
// outline colour two lines below) — `nw-topbar-focus-hc-navy` only on the
// white-surface branch swaps the hc-theme global rule's gold for
// `var(--nw-navy)` (>=3:1 against the white bar; gold measured ~2.38:1
// and was ruled out). The navy-surface branch keeps the global rule's
// gold untouched (already ~7.3:1 against the navy bar).
const compactFocusRingFor = (surface) =>
  surface === "white" ? `${COMPACT_FOCUS_RING} focus-visible:outline-[#0B2E59] nw-topbar-focus-hc-navy` : `${COMPACT_FOCUS_RING} focus-visible:outline-white`;

export function NotificationsBell({ compact = false, surface = "navy" }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const isCoach = user?.role === "coach";
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  /**
   * ISSUE #417's RACE, and why a plain `.then(setState)` is not enough any
   * more.
   *
   * This component now has two sources of loads (mount, and a page
   * announcing it cleared something) AND two optimistic clears (the panel
   * opening, an item being clicked). So a response can land AFTER an
   * optimistic clear it predates, and put the stale badge back — which is
   * exactly what happened: landing on the coach portal marked a player's
   * events read, that triggered a load, and the panel was opened while it
   * was still in flight. The badge cleared and then reappeared reading 3.
   *
   * `seq` numbers every load; `ignoreBefore` is raised to the current seq
   * whenever the reader does something that makes every in-flight answer
   * obsolete. A response whose seq is not above the line is dropped.
   * Nothing is cancelled — the request was already sent — but its answer is
   * no longer allowed to speak for a state it was never asked about.
   */
  const seq = useRef(0);
  const ignoreBefore = useRef(0);

  /**
   * #465's disappear-on-read needs one more piece of bookkeeping: once the
   * `/seen` stamp COMMITS, every row on screen is durably read and must be
   * gone by the NEXT open — but re-fetching while the reader is still
   * looking would empty the panel out from under them, which is the same
   * refusal the #448 note below records for the greyed design. So the
   * refresh is DEFERRED: `seenRefreshPending` is set when the stamp
   * commits while the panel is open, and the close handler spends it.
   * `openNow` mirrors the `open` state into a ref so the stamp's async
   * `.then` can ask "is the panel still open?" without a stale closure.
   */
  const openNow = useRef(false);
  const seenRefreshPending = useRef(false);

  const lastLoadAt = useRef(0);

  const load = useCallback(() => {
    lastLoadAt.current = Date.now();
    const mySeq = (seq.current += 1);
    setLoading(true);
    return getWithRetry("/notifications")
      .then((r) => {
        if (mySeq <= ignoreBefore.current) return;
        setItems(r.data?.items || []);
        setUnread(r.data?.unread_count || 0);
        setFailed(false);
      })
      .catch((e) => {
        // Issue #271, and the same policy Layout.jsx applied to the #185
        // bell: a failed load must never put a WRONG number on a chrome
        // element, so the badge simply doesn't render. But unlike that
        // one, this surface has somewhere to say so — the panel the user
        // deliberately opened — so the failure is stated there with a Try
        // again, exactly as the Dashboard's feedback tile does. Silent in
        // the corner, honest once opened.
        console.error("NotificationsBell: failed to load notifications", e);
        if (mySeq <= ignoreBefore.current) return;
        setItems([]);
        setUnread(0);
        setFailed(isUnknownAnswer(e));
      })
      .finally(() => {
        if (mySeq === seq.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Issue #417: a page cleared some of its own notifications, so re-read.
   *
   * A nudge, not a payload — the event carries nothing and this handler
   * asks the server rather than trusting a number computed elsewhere. That
   * keeps the badge's arithmetic in exactly one place (the API), which is
   * the property that stopped the two watermarks drifting and is worth
   * more here than saving one request.
   *
   * #189 removed this pattern's previous instance (`cc:coach-feedback-seen`)
   * when its last listener went away. This is it returning with a listener
   * that needs it.
   */
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
  }, [load]);

  /**
   * D403 (#1513): reload when the reader comes back to the tab. Skipped
   * while the panel is open: re-reading then would drop the rows the
   * reader is looking at (the #465 refusal recorded in `onOpenChange`), and
   * closing the panel already spends a refresh. Skipped inside the gap so a
   * focus+visibility pair makes one request.
   */
  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== "visible") return;
      if (openNow.current) return;
      if (Date.now() - lastLoadAt.current < RETURN_RELOAD_GAP_MS) return;
      load();
    };
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
    };
  }, [load]);

  const onOpenChange = (next) => {
    setOpen(next);
    openNow.current = next;
    if (!next) {
      /**
       * CLOSING is when the just-seen rows are allowed to leave (#465).
       * The stamp below marked them read; re-reading the feed now — with
       * the panel shut — means the next open shows only what has arrived
       * since, which is the disappear behaviour without a row ever
       * vanishing in front of the reader. Deliberately gated on the stamp
       * having COMMITTED: if every retry failed, no refresh runs, the
       * optimistic state stands for this session, and the next full load
       * re-raises whatever the server still counts — the same worst case
       * #271 already accepted, with no resurrected badge in between.
       */
      if (seenRefreshPending.current) {
        seenRefreshPending.current = false;
        load();
      }
      return;
    }
    seenRefreshPending.current = false;
    track("menu.open", { item: "bell" });

    // Issue #271's PROVEN failure, applied before it can happen again:
    // clearing the badge is a fact about the READER (they are looking at
    // the panel right now) and does not need the server's permission to be
    // true. So the badge clears first, unconditionally, and the durable
    // watermark catches up behind it with a bounded retry. Worst case —
    // every retry fails — the badge is right for this session and the
    // stale server watermark re-raises it on the next full load, where
    // opening the panel stamps it again. No toast: there is nothing for
    // the user to do about it.
    ignoreBefore.current = seq.current;
    setUnread(0);
    /**
     * The BADGE clears; the LIST is left exactly as it is, on purpose. The
     * watermark this stamps does mean every item is read, and under #465
     * read items no longer appear in the feed at all — but emptying the
     * panel out from under the reader in the same frame they opened it
     * would remove the one thing it was opened to show: what they had not
     * yet seen. So the glance in progress keeps its rows, and they leave
     * on the next open instead (the deferred refresh above). This is also
     * what answers #465's vanish-before-seen worry: an item only ever
     * disappears after a panel-open in which it was actually on screen.
     *
     * PR #777 CI finding: "the glance in progress keeps its rows" only
     * holds if some load's rows have actually landed. The `ignoreBefore`
     * line just above discards whichever load was in flight at open
     * time — usually stale-and-rightly-discarded (#417), but when the
     * discarded one is the MOUNT load, opened before it resolved, nothing
     * else was ever going to fetch again: `items` stayed the initial `[]`
     * for the rest of the session (measured on journey 20 under CI load —
     * the server's own answer, moments later, DID carry the new item;
     * this component had already stopped listening for it).
     *
     * The fix is a DIRECT fetch, deliberately NOT routed through `load()`
     * / `seq` / `ignoreBefore` at all, and deliberately touching `items`
     * only, never `unread`. `unread`'s correctness already has its own
     * guard — the optimistic `setUnread(0)` above plus the `seen` handler
     * below — and this fetch has no business relitigating that number.
     *
     * IT MUST RUN — AND SETTLE — BEFORE `/notifications/seen` IS SENT, not
     * merely before its answer is applied. `isUnread` (api's
     * `routes/notifications.ts`) compares each item's timestamp against
     * the LIVE watermark at READ time, so firing this GET and the `seen`
     * POST together leaves their arrival order at the SERVER unspecified —
     * on an unlucky ordering (measured: reintroduced on journey 3, whose
     * feedback item is older than the moment the reader opens the panel)
     * the `seen` write can commit first, and this "recovery" read then
     * sees its OWN item as already-watermarked-read and reports zero, the
     * exact false-empty this fetch exists to prevent. A first version
     * fired both at once and broke exactly that journey. Sequencing them —
     * read the old watermark's answer, THEN stamp the new one — is not a
     * courtesy, it is what makes the read answer a specific question
     * ("what was unread before I said I looked") rather than an ambiguous
     * one.
     */
    getWithRetry("/notifications")
      .then((r) => {
        setItems(r.data?.items || []);
        setFailed(false);
      })
      .catch((e) => {
        // A failed recovery fetch must not stamp a false empty/error state
        // over whatever the mount load (or a previous open) already
        // showed — that load owns reporting a genuine failure through its
        // own `.catch()` (see `load()` above); this one just declines to
        // make things worse.
        console.error("NotificationsBell: failed to refresh the panel on open", e);
      })
      .finally(() => {
        setLoading(false);
        // Idempotent by construction (the server stamps "now"), which is
        // what makes it safe to re-send — and safe to defer to here,
        // whether the read above succeeded or not: the badge's own
        // worst-case tolerance (#271, the comment above `setUnread(0)`)
        // already accepts "every retry failed, stamp it next open".
        postWithRetry("/notifications/seen")
          .then(() => {
            /**
             * ISSUE #448 — the half of the #417 race the seq guard above did
             * not cover, and which left a badge PERMANENTLY wrong.
             *
             * Raising `ignoreBefore` at open time drops answers to loads
             * that were ISSUED before the reader looked. It cannot drop one
             * issued after the clear and SERVED before this write commits —
             * and that load exists: landing on a page marks its
             * notifications read, whose `cc:notifications-changed` nudge
             * fires a load in exactly that window. Measured on journey 17,
             * off the wire: the load went out 40ms before the write landed
             * and came back 146ms after it, still carrying the previous
             * `notifications_seen_at`, and put the dismissed count straight
             * back on the bell. Nothing then corrected it — no further load
             * is triggered — until a full page reload.
             *
             * So raise the line again once the write is durable. Every load
             * still in flight was ASKED of a server that had not been told
             * the reader looked, and its answer no longer speaks for
             * anything. A load issued after this point reads the committed
             * watermark and is believed, as it should be.
             *
             * Deliberately NOT an immediate re-fetch: under #465 that would
             * EMPTY the panel in the same frame the reader opened it, which
             * is the one thing the note above refuses to do. The re-fetch
             * happens — read rows must be gone by the next open — but it
             * waits for the panel to close: scheduled here, spent by
             * `onOpenChange(false)`, or run at once if the reader already
             * closed the panel while the stamp was in flight.
             */
            ignoreBefore.current = seq.current;
            if (openNow.current) {
              seenRefreshPending.current = true;
            } else {
              load();
            }
          })
          .catch((e) => console.error("NotificationsBell: failed to stamp notifications-seen", e));
      });
  };

  const openItem = (item) => {
    setOpen(false);
    openNow.current = false;
    if (!item) return;

    /**
     * Issue #417, the "clicking on it" half. The optimistic-first shape
     * #271 established: this item is read because the reader just clicked
     * it, which is a fact about them and does not need the server's
     * permission to be true. So the item leaves the local list and the
     * badge drops immediately, and the durable row catches up behind it
     * with a bounded retry (`markNotificationsRead`, whose success nudges
     * a reload that gets the same answer from the server). Worst case —
     * every retry fails — the state is right for this session and the next
     * full load re-raises it, where visiting the page marks it again.
     *
     * REMOVED, not greyed (#465): read = gone is the display rule, and
     * every row this panel renders is unread by contract (the feed serves
     * nothing else), so there is no already-read branch to guard any more.
     * The removal is never visible mid-click — `setOpen(false)` above
     * closes the panel in the same commit — so the item is simply absent
     * the next time the panel opens, which is the decided behaviour.
     * `Math.max` still guards the badge against going negative if a stale
     * in-flight answer had lowered it first.
     */
    ignoreBefore.current = seq.current;
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    setUnread((n) => Math.max(0, n - 1));
    markNotificationsRead({ ids: [item.id] });

    // The link is resolved server-side through the ONE mapping in
    // `api/src/lib/notificationLinks.ts` (#189 design point 4). This
    // component deliberately contains no route strings of its own: #80
    // re-homes Training and #79 reworks Upcoming Activities, and a
    // hard-coded destination here would be a second place to forget.
    if (item.link) {
      markNav("bell");
      nav(item.link);
    }
  };

  const label =
    unread > 0
      ? `Notifications: ${unread} unread notification${unread === 1 ? "" : "s"}`
      : "Notifications: no unread notifications";

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {/* Native <button>, not the shadcn ghost Button — #185's reasoning
            holds: the ghost Button has no badge slot, and the badge is
            absolutely positioned against a `relative` parent.
            `h-11 w-11 sm:h-9 sm:w-9` is #106's convention for an icon
            control that must clear a ~44px touch target on a phone without
            bloating desktop density (see TrainingSessions.jsx's TOUCH_ICON);
            it is written out here rather than imported because this is now
            the only such control left in the topbar. */}
        <button
          type="button"
          data-testid="topbar-notifications"
          aria-label={label}
          title="Notifications"
          className={
            compact
              ? // PR #887 review, B3 (D160) — `width: var(--nw-size-tap)` (32 before #1118), no explicit
                // height: `EndControlsRow` (kit/TopBar.jsx) gives this
                // button's own PARENT the bar's full height, and flexbox's
                // default `align-items: stretch` fills this child to match
                // it for free — no negative margin, so this control's own
                // tap area never overlaps its neighbours (the earlier
                // `-m-[13px]` 44px box did: found live, a click just right
                // of the contrast glyph opened the account menu instead).
                // Issue #892 (D171 X2) — the focus-visible outline is
                // `compactFocusRingFor(surface)` above, not the shared
                // `ring-ring`; see that helper's own comment.
                `relative shrink-0 flex items-center justify-center text-[#64748B] ${compactFocusRingFor(surface)}`
              : "relative shrink-0 h-11 w-11 sm:h-9 sm:w-9 rounded-full inline-flex items-center justify-center text-slate-600 hover:bg-accent hover:text-[#0B2E59] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          }
          style={compact ? { width: "var(--nw-size-tap)" } : undefined}
        >
          {compact ? (
            // PR #887 review, B3 — the glyph (and its badge) live inside
            // their OWN small `relative` wrapper here, not the button
            // itself: this button's tap area is 44×(the full bar
            // height), but the badge must still sit at the GLYPH's own
            // top-right corner, not float near the top of a much taller
            // box. The non-compact branch below is UNTOUCHED (badge
            // positioned against the button itself, as shipped) — this
            // wrapper only exists for the new, taller tap target.
            <span className="relative inline-flex">
              <Bell size={18} />
              {unread > 0 && (
                <span
                  data-testid="topbar-notifications-badge"
                  aria-hidden="true"
                  className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center leading-none"
                  style={surface === "white" ? { background: "#0B2E59", color: "#FFFFFF" } : { background: "#FFFFFF", color: "#0B2E59" }}
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </span>
          ) : (
            <>
              <Bell size={18} />
              {/* ABSENT, not a 0, when there is nothing unread (#189 acceptance
                  criterion 1) — a zero badge is noise that trains people to
                  ignore the real one. `aria-hidden` because the count is
                  already in the button's accessible name above; announcing it
                  twice is worse than once. */}
              {unread > 0 && (
                <span
                  data-testid="topbar-notifications-badge"
                  aria-hidden="true"
                  className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center leading-none"
                  style={{ background: "#D4A017", color: "#0B2E59" }}
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>

      {/* `w-[min(20rem,calc(100vw-2rem))]`: at phone-390 a fixed w-80 panel
          plus Radix's collision padding is the topbar-overflow class this
          row keeps being broken by (#251/#263/#273/#276), so the panel is
          told it may shrink instead of pushing the viewport. */}
      <PopoverContent
        align="end"
        collisionPadding={8}
        className="w-[min(20rem,calc(100vw-2rem))] p-0 overflow-hidden"
        data-testid="topbar-notifications-panel"
      >
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Notifications</p>
          <h3 className="font-display text-lg font-bold text-slate-900 leading-tight">What's new</h3>
        </div>

        {failed ? (
          <div className="px-4 py-6 text-center" data-testid="notifications-error">
            <p className="text-sm font-semibold text-slate-900">Couldn't load your notifications.</p>
            <p className="text-xs text-slate-500 mt-1">Check your connection and try again.</p>
            <button
              type="button"
              onClick={load}
              data-testid="notifications-retry"
              className="mt-3 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-[#0B2E59] hover:bg-slate-50 transition-colors"
            >
              Try again
            </button>
          </div>
        ) : loading ? (
          <div className="px-4 py-6 text-center text-sm text-slate-500" data-testid="notifications-loading">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-6 text-center" data-testid="notifications-empty">
            <p className="text-sm font-semibold text-slate-900">You're all caught up.</p>
            {/* Under #465 an empty panel is the NORMAL steady state — the
                list is what's outstanding, so a caught-up reader sees this
                most days and it must read as good news, not as a shrug or
                a failure. A FAILED fetch never lands here: the `failed`
                branch above renders first, with its own copy and a retry
                (#271's false-empty rule, proven by journey 8c). Names what
                this reader will actually be told about, so the empty panel
                is an explanation. #383. */}
            <p className="text-xs text-slate-500 mt-1">
              {isCoach
                ? "Goals your players achieve, reflections they post and completed Identity & Vision will show up here."
                : "Feedback, goals and sessions from your coaches will show up here."}
            </p>
          </div>
        ) : (
          <ul className="max-h-[60vh] overflow-y-auto thin-scroll" data-testid="notifications-list">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => openItem(item)}
                  data-testid="notification-item"
                  data-notification-type={item.type}
                  className="w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:bg-slate-50"
                >
                  {/* min-w-0 + break-words: the title carries coach-written
                      free text (a goal title, a session title). The API
                      caps its length; this is what stops a single long
                      unbroken word from widening the popover.

                      NO read-vs-unread treatment any more (#465): #417
                      carried the distinction in weight, colour, a gold dot
                      and an sr-only prefix, because read rows stayed
                      listed. Read rows are now absent (the feed serves
                      only unread — see the header), so every row gets the
                      unread weight and a per-row marker would be claiming
                      a distinction that no longer exists. The
                      `data-notification-read` probe the journeys asserted
                      went with it — they now assert presence/absence. */}
                  <span className="block min-w-0 text-sm break-words text-slate-900 font-semibold">
                    {item.title}
                  </span>
                  <time dateTime={item.at} className="block text-[11px] text-slate-500 mt-0.5">
                    {relativeTime(item.at)}
                  </time>
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
